import { useEffect, useState } from 'react';
import { Button, Divider, FileDrop, Header, Icon, Select } from '@scuf/common';
import 'styles/main.scss';
import 'styles/ImageManagement.scss';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@scuf/datatable';
import useFetch from 'helpers/hooks/useFetch';
import { notificationAtom, SPARE_PARTS } from 'resources';
import { useSetRecoilState, useRecoilValue } from 'recoil';

type OccupationImage = { occupation: string; image: File | Blob | null; imageCode: string };

const initializeOccupationImages = (count: number): OccupationImage[] => {
    return Array.from({ length: count }, () => ({ occupation: '', image: null, imageCode: '' }));
};

function ImageManagement() {
    const { t } = useTranslation(['spareParts', 'common', 'notification']);
    const [selectedItem, setSelectedItem] = useState<{ value: number; text: string } | null>(null);
    const [items, setItems] = useState<any>([]);
    const [selectItem, setSelectItem] = useState<{ partNumber: string; description: string }[]>([]);
    const [occupationImages, setOccupationImages] = useState<OccupationImage[]>(initializeOccupationImages(3));
    const [filesToSend, setFilesToSend] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const setNotification = useSetRecoilState(notificationAtom);

    const [_, getAllPartNumbers] = useFetch<{ product?: string }, any[]>({
        url: SPARE_PARTS.getAllPartNumbers,
        onError: 'LoadInfo',
        method: 'POST'
    });

    const [, SearchPartNumbers, loadAction] = useFetch<any>({
        url: SPARE_PARTS.searchPartNumbers,
        onError: 'LoadInfo',
        method: 'GET'
    });

    const [, getImages] = useFetch<string[], any>({
        url: SPARE_PARTS.getImages,
        method: 'POST',
        onError: t('notification:critical.ErroImage')
    });

    const [, UploadImagem] = useFetch<any, any>({
        url: SPARE_PARTS.uploadImage + `/${selectedItem?.text}`,
        onError: 'LoadInfo',
        method: 'POST',
        options: {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
    });

    const [, DeleteImage] = useFetch<{ partNumber: string; index: number }, any>({
        url: SPARE_PARTS.deleteImage,
        method: 'POST',
        onError: t('notification:critical.ErroDeleteImage')
    });

    useEffect(() => {
        return () => {
            occupationImages.forEach(item => {
                if (item.image) {
                    URL.revokeObjectURL(URL.createObjectURL(item.image));
                }
            });
        };
    }, [occupationImages]);

    useEffect(() => {
        getAllPartNumbers({}, resp => {
            setItems(
                resp.data?.map((e: any, index: number) => ({
                    value: index,
                    text: e.code
                }))
            );
        });
    }, []);

    const addPartNumber = async () => {
        const params = { filter: selectedItem?.text ?? '', filterType: '1' };

        await SearchPartNumbers(params, resp => {
            const newItem = resp.data
                ? [
                    {
                        description: resp.data[0].description,
                        partNumber: resp.data[0].code
                    }
                ]
                : [];
            setSelectItem(newItem);
        });
        await handleGetImages([selectedItem!.text]);
    };

    const handleFileDrop = (files: File[]) => {
        const updatedOccupationImages = [...occupationImages];
        const newFilesToSend = [...filesToSend];

        for (const file of files) {
            if (!file.type.includes('jpeg') && !file.type.includes('png') && !file.type.includes('jpg')) {
                setNotification(old => ({ ...old, message: t('common:translation.JPEG/PNG'), type: 'critical' }));
                continue;
            }
            const emptyOccupation = updatedOccupationImages.find(item => item.image === null);
            if (emptyOccupation) {
                emptyOccupation.image = file;
                newFilesToSend.push(file);
            }
        }
        setOccupationImages(updatedOccupationImages);
        setFilesToSend(newFilesToSend);
    };

    const handleImageDelete = async (index: number) => {
        if (!selectedItem?.text) return;

        try {
            await DeleteImage(
                {
                    partNumber: selectedItem.text,
                    index
                },
                () => {
                    setNotification(old => ({
                        ...old,
                        message: t('common:translation.ImageDeletedSuccess'),
                        type: 'success'
                    }));
                }
            );

            await handleGetImages([selectedItem.text]);
        } catch (error) {
            setNotification(old => ({
                ...old,
                message: t('notification:critical.ErroDeleteImage'),
                type: 'critical'
            }));
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        const formData = new FormData();
        formData.append('partNumber', selectedItem?.text || '');

        filesToSend.forEach((file, index) => {
            const filename = file.name.split('.');
            const posfix = `_${Date.now()}_${index}.${filename[filename.length - 1]}`;
            const prefix = filename.slice(0, -1).join('.');
            const newName = prefix + posfix;
            const newFile = new File([file], newName, { type: file.type });
            formData.append('formFile', newFile);
        });

        try {
            await UploadImagem(formData);
            setNotification(old => ({ ...old, message: t('common:translation.ImageUploadSuccess'), type: 'success' }));
        } catch (error) {
            setNotification(old => ({ ...old, message: t('common:translation.ImageUploadError'), type: 'critical' }));
        } finally {
            setIsLoading(false);
            setOccupationImages(initializeOccupationImages(3));
            setFilesToSend([]);
            await handleGetImages([selectedItem!.text]);
        }
    };

    const clearFilter = () => {
        setSelectedItem(null);
        setSelectItem([]);
    };

    const handleGetImages = async (partNumbers: string[]): Promise<void> => {
        await getImages(partNumbers, resp => {
            const imagesData = resp.data || [];

            const updatedOccupationImages = initializeOccupationImages(3);
            const filesToSendArray: File[] = [];

            imagesData.forEach((imageData: any) => {
                if (imageData.images && imageData.images.length > 0) {
                    const imagesToProcess = imageData.images.slice(0, 3);

                    imagesToProcess.forEach((base64String: string, index: number) => {
                        if (index < updatedOccupationImages.length) {
                            const byteCharacters = atob(base64String);
                            const byteNumbers = new Uint8Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const blob = new Blob([byteNumbers], { type: 'image/jpeg' });
                            const fileName = `${imageData.partNumber}_${index}.jpg`;
                            const file = new File([blob], fileName, { type: 'image/jpeg' });

                            updatedOccupationImages[index] = {
                                ...updatedOccupationImages[index],
                                image: file,
                                occupation: imageData.partNumber,
                                imageCode: base64String
                            };

                            filesToSendArray.push(file);
                        }
                    });
                } else {
                    console.warn(`No images found for part number: ${imageData.partNumber}`);
                }
            });
            setFilesToSend(filesToSendArray);
            setOccupationImages(updatedOccupationImages);
        });
    };

    const isSubmitDisabled = filesToSend.length === 0 || isLoading;
    const isAddPNDisabled = !selectedItem || !selectedItem.text;
    const isAddFilterDisabled = selectItem.length === 0;

    return (
        <div>
            <div className="container-general-items-img">
                <div className="search-items">
                    <Select
                        label={t('common:translation.Item')}
                        options={items}
                        value={selectedItem?.value ?? null}
                        loading={items.length === 0}
                        search={true}
                        placeholder={t('common:translation.Item')}
                        onChange={e => setSelectedItem({ value: e, text: items[e].text })}
                    />
                    <div className="buttonFilter">
                        <Button
                            loading={loadAction}
                            icon="slidercontrols-plus"
                            onClick={() => addPartNumber()}
                            disabled={isAddPNDisabled}
                            content={t('maintenancereport:translation.AddPN')}
                        />
                        <Button
                            type="secondary"
                            icon="badge-delete"
                            onClick={() => clearFilter()}
                            disabled={isAddFilterDisabled}
                            content={t('common:actions.ResetFilters')}
                        />
                    </div>
                </div>
                <div className="table-items-img">
                    <DataTable data={selectItem} rows={1} scrollable virtualScroll scrollHeight="100px">
                        <DataTable.Column field="partNumber" header={t('common:translation.PartNumber')} />
                        <DataTable.Column field="description" header={t('common:translation.Description')} />
                    </DataTable>
                </div>
                {selectItem.length > 0 && (
                    <>
                        <div className="form-group">
                            <FileDrop
                                acceptedTypes={['.png', '.jpeg', '.jpg']}
                                resetButtonText={t('common:actions.Clean')}
                                multiple={false}
                                buttonText={t('common:actions.BrowseFiles')}
                                label={t('common:translation.UploadImageFile')}
                                placeholder={t('common:actions.Draganddropfileshere')}
                                onAccepted={handleFileDrop}
                            />
                        </div>
                        <div className="image-previews">
                            {occupationImages.map((item, index) => (
                                <div key={index} className="image-preview" style={{ position: 'relative', marginBottom: '10px' }}>
                                    {item.image && (
                                        <>
                                            <img
                                                src={
                                                    item.imageCode
                                                        ? `data:image/jpeg;base64,${item.imageCode}`
                                                        : item.image
                                                            ? URL.createObjectURL(item.image)
                                                            : ''
                                                }
                                                //src={`data:image/jpeg;base64,${item.imageCode}`}
                                                alt={`preview-${index}`}
                                            />
                                            <div
                                                className="image-actions"
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0
                                                }}
                                            >
                                                <Icon
                                                    name="delete"
                                                    size="medium"
                                                    onClick={() => handleImageDelete(index)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        color: 'red',
                                                        background: 'rgba(0, 0, 0, 0.5)',
                                                        borderRadius: '50%'
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="form-group" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                            <Button
                                name="primary"
                                content={t('common:translation.UploadImageFile')}
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className="button-upload"
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ImageManagement;
